import { ApplicationRef, ComponentRef, createComponent, EmbeddedViewRef, EnvironmentInjector } from '@angular/core';
import { CollabFileType } from '@common/collab/enums/collab-file-type.enum';
import { resolvePath } from '@common/util/path';
import { FlareTextEditorComponent } from '@portal-core/project-files/components/flare-text-editor/flare-text-editor.component';
import { EditorEvent } from '@portal-core/project-files/events/editor-event.event';
import { ReviewFilesService } from '@portal-core/reviews/review-files/services/review-files.service';
import { SnippetNodeViewOptions } from '@portal-core/text-editor/node-views/models/snippet-node-view-options.model';
import { NodeViewBase } from '@portal-core/text-editor/node-views/node-view.base';
import { GetPosForNodeView } from '@portal-core/text-editor/node-views/types/nodeview-get-pos.type';
import { TextEditorApiService } from '@portal-core/text-editor/services/text-editor-api.service';
import { BidiBridge } from '@portal-core/util/bidi-bridge';
import { ProseMirrorNode } from 'prosemirror-model';
import { Decoration, EditorView, NodeView } from 'prosemirror-view';
import { Subscription } from 'rxjs';

export class SnippetNodeView extends NodeViewBase implements NodeView {
  private componentRef: ComponentRef<FlareTextEditorComponent>;
  private component: FlareTextEditorComponent;
  private componentSubscriptions = new Subscription();
  private isRecursive = false;
  /** Whether or not the editor has successfully loaded. */
  private loaded = false;
  private reviewFileSubscription: Subscription;
  private snippetContentSubscription: Subscription;

  constructor(
    node: ProseMirrorNode,
    editorView: EditorView,
    getPos: GetPosForNodeView,
    decorations: Decoration[],
    nodeViewBridge: BidiBridge<EditorEvent, EditorEvent>,
    private injector: EnvironmentInjector,
    private appRef: ApplicationRef,
    private reviewFilesService: ReviewFilesService,
    private textEditorApiService: TextEditorApiService,
    private options: SnippetNodeViewOptions
  ) {
    super(node, editorView, getPos, decorations, nodeViewBridge);

    this.dom.setAttribute('contenteditable', 'false');

    // Create the component reference
    this.componentRef = createComponent(FlareTextEditorComponent, {
      environmentInjector: this.injector
    });

    // Attach to ApplicationRef for change detection
    this.appRef.attachView(this.componentRef.hostView);

    // Insert into the DOM
    this.dom.appendChild((this.componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0]);

    this.component = this.componentRef.instance;

    // Listen to the load event to notify the editor that the node view size may have changed
    this.componentSubscriptions.add(
      this.component.load.subscribe(success => {
        if (success) {
          this.loaded = true;
        }

        // Only display an error if the editor has not successfully loaded yet
        this.setText(success || this.loaded ? null : 'Unable to load snippet.');
        this.emitEvent('node-view-size-changed', { nodeName: node.type.name });
      })
    );

    // Initialize the component
    this.componentRef.setInput('branchName', this.options.branchName());
    this.componentRef.setInput('commitId', this.options.commitId());
    // Render html and body nodes as different tags so that the html and body styles are not applied to them.
    // This makes the snippets render more like they would in the output since the html/body tags are not rendered with the snippet in output.
    this.componentRef.setInput('editorTagNames', {
      'html': 'mc-lwe-snippet-html',
      'body': 'mc-lwe-snippet-body'
    });
    this.componentRef.setInput('editorType', 'collab');
    this.componentRef.setInput('fileType', this.options.fileType());
    this.componentRef.setInput('gutterEnabled', false);
    this.componentRef.setInput('licenseUser', this.options.licenseUser());
    this.componentRef.setInput('parentComputedStylesheetClassNames', this.options.parentComputedStylesheetClassNames());
    this.componentRef.setInput('parentFilePaths', [...this.options.parentFilePaths() ?? [], this.options.filePath()]);
    this.componentRef.setInput('projectId', this.options.projectId());
    this.componentRef.setInput('projectLanguage', this.options.projectLanguage());
    this.componentRef.setInput('readonly', true);
    this.componentRef.setInput('reviewPackageId', this.options.reviewPackageId());
    this.componentRef.setInput('showAI', false);
    this.componentRef.setInput('showConditions', this.options.showConditions());
    this.componentRef.setInput('showStatusBar', false);
    this.componentRef.setInput('showTagbar', false);
    this.componentRef.setInput('showTrackedChanges', false);
    this.componentRef.setInput('stylesEnabled', this.options.stylesEnabled());
    this.componentRef.setInput('timelineEnabled', false);
    this.componentRef.setInput('userDataEnabled', false);

    // Sync the node
    this.syncNode(node);
  }

  destroy() {
    super.destroy();
    this.componentSubscriptions.unsubscribe();
    this.reviewFileSubscription?.unsubscribe();
    this.snippetContentSubscription?.unsubscribe();

    if (this.componentRef) {
      this.appRef.detachView(this.componentRef.hostView);
      this.componentRef.destroy();
      this.componentRef = null;
      this.component = null;
    }
  }

  onEditorEvent(event: EditorEvent) {
    if (event.type === 'stylesEnabled-changed') {
      this.componentRef.setInput('stylesEnabled', event.data.stylesEnabled);
      this.componentRef.setInput('parentComputedStylesheetClassNames', event.data.computedStylesheetClassNames);
    } else if (event.type === 'showConditions-changed') {
      this.componentRef.setInput('showConditions', event.data.showConditions);
    } else if (event.type === 'commitId-changed') {
      this.componentRef.setInput('commitId', event.data.commitId);
    }
  }

  syncNode(node: ProseMirrorNode) {
    // Resolve the full path of the snippet in the project for use by the nodeViews and plugins
    const resolvedPath = resolvePath(this.options.filePath() ?? '', node.attrs.src ?? '');

    // Get the source link for files that are in a project but not included in the collab server
    // Only rebuild if the path has changed. New node views cause the editor to be recreated, which is expensive.
    if (resolvedPath !== this.component.filePath) {
      this.loaded = false;
      this.setText('Loading...');
      this.componentRef.setInput('filePath', resolvedPath);
      this.isRecursive = this.options.parentFilePaths().includes(resolvedPath) || this.options.filePath() === resolvedPath;
      this.reviewFileSubscription?.unsubscribe();

      if (this.options.fileType() === CollabFileType.Review && this.options.licenseUser()?.LicenseId) {
        this.reviewFileSubscription = this.reviewFilesService.getReviewFileByPackageIdAndFilePath$(this.options.licenseUser().LicenseId, this.options.reviewPackageId(), resolvedPath).subscribe(reviewFile => {
          this.componentRef.setInput('fileId', reviewFile?.Id);
          this.updateEditorModeAndContent();
        });
      } else {
        this.componentRef.setInput('fileId', null);
        this.updateEditorModeAndContent();
      }
    }
  }

  private updateEditorModeAndContent() {
    const hasFileId = Number.isInteger(this.component.fileId);
    const isNotCollabFile = !hasFileId && this.component.fileType === CollabFileType.Review;
    this.componentRef.setInput('editorType', isNotCollabFile || this.isRecursive ? 'solo' : 'collab');

    if (this.isRecursive) {
      this.snippetContentSubscription?.unsubscribe();
      this.setText('Recursive snippet detected. Cannot load snippet content.');
    }
    else if (isNotCollabFile) {
      this.loadSnippetContent();
    } else {
      this.componentRef.setInput('content', null);
      this.snippetContentSubscription?.unsubscribe();
      if (!this.node?.attrs?.src) {
        this.setText('The snippet src attribute is missing or invalid.')
      }
    }
  }

  private loadSnippetContent() {
    const contentPath = this.node?.attrs?.src ?? '';
    const src = contentPath ? this.options.buildContentApiUri(contentPath) : '';

    this.snippetContentSubscription?.unsubscribe();

    if (!src) {
      this.setText('The snippet src attribute is missing or invalid.');
      return;
    }

    this.snippetContentSubscription = this.textEditorApiService.getFlareSnippet$(src).subscribe({
      next: content => {
        this.componentRef.setInput('content', content);
      },
      error: error => {
        console.error('Failed to load snippet content', error);
        this.setText('Unable to load snippet.');
      }
    });
  }

  private setText(text: string) {
    if (text) {
      this.dom.setAttribute('mc-lwe-snippet-text', text);
    } else {
      this.dom.removeAttribute('mc-lwe-snippet-text');
    }
  }
}
